/**
 * SOVRANOVA — Settings Context
 * Global user preferences: voice on/off, text-only mode, autoplay.
 * Persisted to localStorage so they survive reloads and work offline.
 *
 * Defaults: voice ON, autoplay ON (voice-first is the SOVRANOVA default).
 * Text-only mode is the accessibility opt-out.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { DEFAULT_VOICE_ID } from '../services/voices'

const DEFAULTS = {
  voiceEnabled: true,    // AI speaks responses aloud
  autoplayVoice: true,   // voice starts automatically where allowed
  textOnly: false,       // accessibility: no voice anywhere, text only
  companionOnDashboard: true, // show the listening companion on the dashboard
  voiceId: DEFAULT_VOICE_ID,  // ElevenLabs spoken-guidance voice (default: Sarah)
  humeVoiceId: '',            // Live Hume conversation voice ('' = app default config voice)
  pushNotifications: false,   // web push (Task 5) — off by default; hidden entirely in
                               // survivor-safe/disguise mode (see hooks/useDisguiseMode.js)
}

const STORAGE_KEY = 'sovranova_settings_v1'

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) }
  } catch {}
  return { ...DEFAULTS }
}

const SettingsContext = createContext({
  settings: DEFAULTS,
  setSetting: () => {},
  resetSettings: () => {},
})

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(loadSettings)

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)) } catch {}
  }, [settings])

  const setSetting = useCallback((key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value }
      // textOnly is a master switch — turning it on disables all voice
      if (key === 'textOnly' && value === true) {
        next.voiceEnabled = false
        next.autoplayVoice = false
        next.companionOnDashboard = false
      }
      // turning textOnly off restores voice defaults
      if (key === 'textOnly' && value === false) {
        next.voiceEnabled = true
        next.autoplayVoice = true
        next.companionOnDashboard = true
      }
      return next
    })
  }, [])

  const resetSettings = useCallback(() => setSettings({ ...DEFAULTS }), [])

  return (
    <SettingsContext.Provider value={{ settings, setSetting, resetSettings }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  return useContext(SettingsContext)
}
